/**
 * The Pictures view's top row (spec 027, 026's `topRow`): the generation
 * status line, then the story's one look (D-20, D-27): the style's name,
 * the text, the palette and, for an unedited preset, that preset's sample
 * thumbnail (D-19), with "Edit look" (the style picker) and "Copy as style
 * guide", both in every mode, off included. The look is the art style only
 * (D-25): no framing, size, background or transparency lines and no version.
 * While some of the story's generated pictures are in the previous look
 * (`oldLook`, D-26) it shows that line with Discard and Discard and draft
 * new, and after Edit look saves a changed look it asks once (D-30). Neither
 * is a gate (D-31).
 */
import React, { useEffect, useState } from 'react';
import { lookPreset, styleGuideText, type ImageOwnerRef, type Look } from '../../../../Shared/src/images';
import { useImageGenStore } from '../../stores/imageGenStore';
import { DISCARD_TEXT, DiscardOldLookModal, OldLookLine, undoDiscard } from './DiscardOldLook';
import LookPicker from './LookPicker';
import { LOOK_SAMPLE_URLS } from './lookSamples';
import { IMAGE_TEXT, statusLineText } from './imageGenText';
import styles from './ImageGen.module.scss';

export const LOOK_ROW_TEXT = {
  title: 'Look',
  none: 'No look yet. The first Generate asks for one.',
  edit: 'Edit look',
  copy: 'Copy as style guide',
  copied: 'Copied',
  own: 'Your own style',
  edited: 'Edited',
} as const;

/** The style's name (D-27): a preset's name and subtitle, "Your own style", or "Edited". */
export function lookStyleName(look: Pick<Look, 'presetId' | 'text'>): string {
  if (look.presetId === 'own') return LOOK_ROW_TEXT.own;
  const preset = look.presetId ? lookPreset(look.presetId) : undefined;
  if (preset && preset.text === look.text) return `${preset.name} · ${preset.subtitle}`;
  return LOOK_ROW_TEXT.edited;
}

/** The row for a story (027, `storyId`) or for any owner (spec 030, `owner`). */
export const LookRow: React.FC<{ storyId?: number; owner?: ImageOwnerRef }> = ({ storyId, owner: given }) => {
  const owner: ImageOwnerRef = given ?? { kind: 'story', id: storyId ?? 0 };
  const settings = useImageGenStore((s) => s.settings);
  const view = useImageGenStore((s) => s.look);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  // The discard prompt after a look change, and the "N pictures discarded" line with its Undo.
  const [prompting, setPrompting] = useState(false);
  const [discarded, setDiscarded] = useState<number | null>(null);

  useEffect(() => {
    if (owner.kind === 'story') {
      if (useImageGenStore.getState().storyId !== owner.id) void useImageGenStore.getState().setStory(owner.id);
    } else void useImageGenStore.getState().setOwner(owner);
  }, [owner.kind, owner.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const look = view?.look;
  const oldLook = view?.oldLook;

  const onDiscarded = (n: number) => {
    setPrompting(false);
    setDiscarded(n);
  };
  const undo = async () => {
    setDiscarded(null);
    await undoDiscard();
  };

  const copy = async () => {
    if (!look) return;
    try {
      await navigator.clipboard.writeText(styleGuideText(look));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const preset = look?.presetId && look.presetId !== 'own' ? lookPreset(look.presetId) : undefined;
  const unedited = !!look && !!preset && preset.text === look.text;

  return (
    <div className={styles.lookRow} data-testid="look-row">
      <p className={styles.statusLine} data-testid="image-status-line">{statusLineText(settings)}</p>
      <div className={styles.lookHead}>
        <span className={styles.lookTitle}>{LOOK_ROW_TEXT.title}</span>
        {look ? (
          <span className={styles.muted} data-testid="look-style">{lookStyleName(look)}</span>
        ) : (
          <span className={styles.muted}>{LOOK_ROW_TEXT.none}</span>
        )}
      </div>
      {unedited && preset && (
        <img className={styles.lookThumb} src={LOOK_SAMPLE_URLS[preset.id]} alt={`${preset.name} sample`} data-testid="look-row-sample" />
      )}
      {look && <p data-testid="look-row-text">{look.text}</p>}
      {look?.palette && look.palette.length > 0 && (
        <div className={styles.swatches} data-testid="look-palette">
          {look.palette.map((c) => <span key={c} className={styles.swatch} style={{ background: c }} title={c} />)}
        </div>
      )}
      <div className={styles.row}>
        <button type="button" className={styles.button} onClick={() => setEditing(true)} data-testid="look-edit">
          {LOOK_ROW_TEXT.edit}
        </button>
        {look && (
          <button type="button" className={styles.button} onClick={() => { void copy(); }} data-testid="look-copy">
            {copied ? LOOK_ROW_TEXT.copied : LOOK_ROW_TEXT.copy}
          </button>
        )}
      </div>
      {oldLook && oldLook.slotIds.length > 0 && <OldLookLine oldLook={oldLook} onDiscarded={onDiscarded} />}
      {discarded !== null && (
        <div className={styles.row} data-testid="look-discarded">
          <span>{DISCARD_TEXT.discarded(discarded)}</span>
          <button type="button" className={styles.button} onClick={() => { void undo(); }} data-testid="look-discard-undo">
            {IMAGE_TEXT.undo}
          </button>
        </div>
      )}
      {editing && (
        <LookPicker
          owner={owner}
          from="edit"
          onClose={() => setEditing(false)}
          onSaved={(changed) => {
            if (changed && useImageGenStore.getState().look?.oldLook) {
              setDiscarded(null);
              setPrompting(true);
            }
          }}
        />
      )}
      {prompting && oldLook && (
        <DiscardOldLookModal oldLook={oldLook} onClose={() => setPrompting(false)} onDiscarded={onDiscarded} />
      )}
    </div>
  );
};

export default LookRow;
